import {
  authApi,
  authService,
  messagingApi,
  messagingApi2,
  organizationApi,
  organizationApi2,
  payrollApi,
  schedulingApi,
  schedulingApi2,
} from '@/api';
import * as React from 'react';
import {
  AlertStore,
  AreaStore,
  DepartmentStore,
  EducationalDocumentStore,
  EmployeeStore,
  EntityMap,
  NoticeStore,
  OnboardingDocumentEmployeeStore,
  OnboardingDocumentStore,
  OnboardingFormEmployeeFileStore,
  OnboardingFormEmployeeStore,
  OnboardingFormStore,
  PayrollStore,
  PositionStore,
  RequestsStore,
  RotationStore,
  ScheduleStore,
  ShiftPlanStore,
  ShiftStore,
  ShiftSwapStore,
  SickNoticeStore,
  StaffStore,
  SupplementaryRateStore,
  TemporaryEmployeeStore,
  TimeOffStore,
  TimesheetStore,
  UserStore,
  WageStore,
} from 'sol-data';
import { ScheduleComponentStore } from './schedule-component-store';

export const entityMap = new EntityMap();

const alertStore = new AlertStore(messagingApi2);
const areaStore = new AreaStore(organizationApi);
const departmentStore = new DepartmentStore(organizationApi);
const employeeStore = new EmployeeStore(organizationApi2, authApi);
const payrollStore = new PayrollStore(payrollApi);
const positionStore = new PositionStore(organizationApi, employeeStore);
const rotationStore = new RotationStore(organizationApi);
const scheduleComponentStore = new ScheduleComponentStore();
const scheduleStore = new ScheduleStore(schedulingApi2);
const shiftPlanStore = new ShiftPlanStore(schedulingApi);
const shiftStore = new ShiftStore(schedulingApi2, employeeStore, entityMap);
const shiftSwapStore = new ShiftSwapStore(schedulingApi2, messagingApi, entityMap);
const sickNoticeStore = new SickNoticeStore(schedulingApi2, entityMap);
const timeOffStore = new TimeOffStore(schedulingApi2, entityMap);
const timesheetStore = new TimesheetStore(payrollApi);
const staffStore = new StaffStore(organizationApi);
const userStore = new UserStore(authService, authApi);
const supplementaryRateStore = new SupplementaryRateStore(organizationApi);
const requestsStore = new RequestsStore(
  schedulingApi2,
  sickNoticeStore,
  timeOffStore,
  shiftSwapStore,
);
const noticeStore = new NoticeStore(organizationApi2, messagingApi);
const educationDocumentStore = new EducationalDocumentStore(organizationApi2);
const wageStore = new WageStore(organizationApi);
const onboardingDocumentStore = new OnboardingDocumentStore(organizationApi2);
const onboardingDocumentEmployeeStore = new OnboardingDocumentEmployeeStore(organizationApi2);
const onboardingFormStore = new OnboardingFormStore(organizationApi2);
const onboardingFormEmployeeStore = new OnboardingFormEmployeeStore(organizationApi2);
const temporaryEmployeeStore = new TemporaryEmployeeStore(organizationApi2);
const onboardingFormEmployeeFileStore = new OnboardingFormEmployeeFileStore(organizationApi2);

entityMap.addAll([
  areaStore,
  departmentStore,
  employeeStore,
  employeeStore.userLookup,
  payrollStore.items,
  payrollStore.holidays,
  payrollStore.limits,
  positionStore,
  rotationStore,
  scheduleStore,
  shiftPlanStore,
  shiftStore,
  shiftSwapStore,
  sickNoticeStore,
  timeOffStore,
  timesheetStore,
  noticeStore,
  educationDocumentStore,
  wageStore,
  supplementaryRateStore,
  alertStore,
  onboardingDocumentStore,
  onboardingDocumentEmployeeStore,
  onboardingFormStore,
  onboardingFormEmployeeStore,
  temporaryEmployeeStore,
]);

export const stores = {
  areaStore,
  departmentStore,
  employeeStore,
  payrollStore,
  positionStore,
  rotationStore,
  scheduleComponentStore,
  scheduleStore,
  shiftPlanStore,
  shiftStore,
  shiftSwapStore,
  sickNoticeStore,
  timeOffStore,
  timesheetStore,
  userStore,
  requestsStore,
  noticeStore,
  educationDocumentStore,
  wageStore,
  staffStore,
  supplementaryRateStore,
  alertStore,
  onboardingDocumentStore,
  onboardingDocumentEmployeeStore,
  onboardingFormStore,
  onboardingFormEmployeeStore,
  temporaryEmployeeStore,
  onboardingFormEmployeeFileStore,
};

export {
  AlertStore,
  AreaStore,
  DepartmentStore,
  EducationalDocumentStore,
  EmployeeStore,
  NoticeStore,
  OnboardingFormEmployeeFileStore,
  PayrollStore,
  PositionStore,
  RequestsStore,
  RotationStore,
  ScheduleComponentStore,
  ScheduleStore,
  ShiftPlanStore,
  ShiftStore,
  ShiftSwapStore,
  SickNoticeStore,
  staffStore,
  SupplementaryRateStore,
  TemporaryEmployeeStore,
  TimeOffStore,
  TimesheetStore,
  UserStore,
  WageStore,
};

export const StoresContext = React.createContext<typeof stores | null>(null);

export const useStores = () => {
  const stores = React.useContext(StoresContext);

  if (!stores) {
    throw new Error('stores context was not found - StoreProvider was not used');
  }

  return stores;
};

// cheeky addition to `window` for dev, helpful to see state updates easily
if (process.env.NODE_ENV === 'development') {
  (window as any).stores = stores;
}
